import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import Swal from 'sweetalert2';
import { apiUrl } from '../../config/api';
import logo from '../../images/amazing gems and jewellery png with name.png';
import './LoginModal.scss';

const MIN_PASSWORD_LENGTH = 8;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const INITIAL_FORM = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  contactNo: '',
};

/**
 * Sign-up with e-mail OTP verification.
 *
 * The previous implementation drove the three steps by reaching into
 * the DOM (`document.querySelector('.verifyEmail').style.display`).
 * Because GuestLoginModal used the *same* class names and both modals
 * were mounted at once, those queries regularly hit the other modal's
 * elements. The flow is a `step` state machine now.
 */
const SignupModal = ({ show, onClose }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState('details'); // details -> otp -> verified
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset when the modal is dismissed, so re-opening does not resume
  // half-way through a previous attempt.
  useEffect(() => {
    if (!show) {
      setForm(INITIAL_FORM);
      setErrors({});
      setStep('details');
      setOtp('');
      setShowPassword(false);
      setIsSubmitting(false);
    }
  }, [show]);

  const setField = (field) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Please enter your name.';
    if (!EMAIL_PATTERN.test(form.email.trim())) next.email = 'Please enter a valid email address.';
    if (form.password.length < MIN_PASSWORD_LENGTH) {
      next.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    if (form.password !== form.confirmPassword) {
      next.confirmPassword = 'Passwords do not match.';
    }
    if (!form.contactNo) next.contactNo = 'Please enter a contact number.';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSendOtp = async () => {
    if (!validate() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(apiUrl('/api/send-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim() }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        Swal.fire({
          icon: 'warning',
          confirmButtonColor: '#a17e33',
          title: 'Could not send the code',
          text: data.message || 'Please check the email address and try again.',
        });
        return;
      }

      setStep('otp');
      setOtp('');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        confirmButtonColor: '#a17e33',
        title: 'Network error',
        text: 'We could not reach the server. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    if (!otp.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(apiUrl('/api/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim(), otp: otp.trim() }),
      });

      if (!response.ok) {
        Swal.fire({
          icon: 'warning',
          confirmButtonColor: '#a17e33',
          title: 'That code did not match',
          text: 'Please check the code, or request a new one.',
        });
        setOtp('');
        return;
      }

      setStep('verified');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        confirmButtonColor: '#a17e33',
        title: 'Network error',
        text: 'We could not verify the code. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    if (step !== 'verified' || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(apiUrl('/api/user/signup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          confirm_password: form.confirmPassword,
          contact_no: form.contactNo,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!data.success) {
        Swal.fire({
          icon: 'warning',
          confirmButtonColor: '#a17e33',
          title: 'Sign up failed',
          text: data.message || 'Please review your details and try again.',
        });
        return;
      }

      onClose();
      Swal.fire({
        icon: 'success',
        confirmButtonColor: '#a17e33',
        title: 'Account created',
        text: 'Log in to continue.',
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        confirmButtonColor: '#a17e33',
        title: 'Network error',
        text: 'We could not create your account. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered scrollable>
      <Modal.Header closeButton>
        <span className="auth-modal__brand-label">Create account</span>
      </Modal.Header>
      <Modal.Body>
        <div className="auth-modal">
          <img className="auth-modal__logo" src={logo} alt="Bansal Attire" />
          <h2 className="auth-modal__title">Sign Up</h2>

          <form className="auth-form" onSubmit={handleSignup} noValidate>
            <div className="field">
              <label className="field__label" htmlFor="signup-name">
                Full name
              </label>
              <input
                id="signup-name"
                className="input"
                type="text"
                value={form.name}
                onChange={setField('name')}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? 'signup-name-error' : undefined}
                disabled={step !== 'details' || isSubmitting}
                autoComplete="name"
              />
              {errors.name && (
                <span className="field__error" id="signup-name-error">
                  {errors.name}
                </span>
              )}
            </div>

            <div className="field">
              <label className="field__label" htmlFor="signup-email">
                Email
              </label>
              <input
                id="signup-email"
                className="input"
                type="email"
                value={form.email}
                onChange={setField('email')}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'signup-email-error' : undefined}
                disabled={step !== 'details' || isSubmitting}
                autoComplete="email"
              />
              {errors.email && (
                <span className="field__error" id="signup-email-error">
                  {errors.email}
                </span>
              )}
            </div>

            <div className="field">
              <label className="field__label" htmlFor="signup-password">
                Password
              </label>
              <div className="input-with-affix">
                <input
                  id="signup-password"
                  className="input"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={setField('password')}
                  aria-invalid={Boolean(errors.password)}
                  aria-describedby={errors.password ? 'signup-password-error' : 'signup-password-hint'}
                  disabled={step !== 'details' || isSubmitting}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="input-with-affix__button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={step !== 'details' || isSubmitting}
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password ? (
                <span className="field__error" id="signup-password-error">
                  {errors.password}
                </span>
              ) : (
                <span className="field__hint" id="signup-password-hint">
                  At least {MIN_PASSWORD_LENGTH} characters.
                </span>
              )}
            </div>

            <div className="field">
              <label className="field__label" htmlFor="signup-confirm">
                Confirm password
              </label>
              <input
                id="signup-confirm"
                className="input"
                type="password"
                value={form.confirmPassword}
                onChange={setField('confirmPassword')}
                aria-invalid={Boolean(errors.confirmPassword)}
                aria-describedby={errors.confirmPassword ? 'signup-confirm-error' : undefined}
                disabled={step !== 'details' || isSubmitting}
                autoComplete="new-password"
              />
              {errors.confirmPassword && (
                <span className="field__error" id="signup-confirm-error">
                  {errors.confirmPassword}
                </span>
              )}
            </div>

            <div className="field">
              <label className="field__label" htmlFor="signup-phone">
                Contact number
              </label>
              <PhoneInput
                id="signup-phone"
                placeholder="Enter phone number"
                value={form.contactNo}
                onChange={(value) => setField('contactNo')(value ?? '')}
                className="phone-input"
                disabled={step !== 'details' || isSubmitting}
              />
              {errors.contactNo && <span className="field__error">{errors.contactNo}</span>}
            </div>

            {step === 'details' && (
              <button
                type="button"
                className="btn btn--primary btn--block"
                onClick={handleSendOtp}
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="btn__spinner" aria-hidden="true" />
                    Sending code…
                  </>
                ) : (
                  'Verify email'
                )}
              </button>
            )}

            {step === 'verified' && (
              <>
                <p className="alert alert--success" role="status">
                  Email verified — you&apos;re ready to create your account.
                </p>
                <button
                  type="submit"
                  className="btn btn--primary btn--block"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="btn__spinner" aria-hidden="true" />
                      Creating account…
                    </>
                  ) : (
                    'Create account'
                  )}
                </button>
              </>
            )}
          </form>

          {step === 'otp' && (
            <form className="auth-form auth-form--otp" onSubmit={handleVerifyOtp}>
              <p className="field__hint">
                We sent a 6-digit code to <strong>{form.email}</strong>.
              </p>
              <div className="field">
                <label className="field__label" htmlFor="signup-otp">
                  Verification code
                </label>
                <input
                  id="signup-otp"
                  className="input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(event) => setOtp(event.target.value)}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn--primary btn--block"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="btn__spinner" aria-hidden="true" />
                    Verifying…
                  </>
                ) : (
                  'Verify code'
                )}
              </button>
              <button
                type="button"
                className="btn btn--link btn--block"
                onClick={handleSendOtp}
                disabled={isSubmitting}
              >
                Resend code
              </button>
            </form>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SignupModal;
